export enum LogLevel {
  always = 0,
  trace = 100,
  debug = 200,
  log = 300,
  info = 300,
  warn = 400,
  error = 500,
  never = 1000,
}

export type LogLevelString = 'always' | 'trace' | 'debug' | 'log' | 'info' | 'warn' | 'error' | 'never';
export const LogLevelStringValues: Array<LogLevelString> = [
  'always',
  'trace',
  'debug',
  'log',
  'info',
  'warn',
  'error',
  'never',
];

export function parseLogLevel(value: string | null): LogLevel | null {
  switch (value) {
    case 'always':
      return LogLevel.always;
    case 'trace':
      return LogLevel.trace;
    case 'debug':
      return LogLevel.debug;
    case 'log':
      return LogLevel.log;
    case 'info':
      return LogLevel.info;
    case 'warn':
      return LogLevel.warn;
    case 'error':
      return LogLevel.error;
    case 'never':
      return LogLevel.never;
    default:
      return null;
  }
}

export function parseLogLevelSafe(value: LogLevelString): LogLevel {
  const level = parseLogLevel(value);
  if (level == null) {
    throw new Error(`Unknown log level ${value}`);
  }
  return level;
}

export type LogMessageMetadata = Record<string, unknown>;
export type LogMessageMetadataCallback = () => Record<string, unknown> | undefined;

export interface InternalLogMessage extends LogMessageMetadata {
  level: LogLevel;
  ts: number;
  message: string;
  logger: string;
}

export interface ILogger {
  level: LogLevel;
  /**
   * Log a message with the specified level. If the provided level is above or equal to the logger's
   * level, the metadataCallback is invoked to generate additional metadata to associate with the log
   * message. This metadata is combined with the logger's metadata before it is forwarded to the router.
   * @param level
   * @param message
   * @param metadataCallback
   */
  log: (level: LogLevel, message: string, metadataCallback?: LogMessageMetadataCallback) => ILogger;

  /**
   * Log a message with the trace level. See Logger.log for more information.
   * @param message
   * @param metadataCallback
   */
  trace: (message: string, metadataCallback?: LogMessageMetadataCallback) => ILogger;

  /**
   * Log a message with the debug level. See Logger.log for more information.
   * @param message
   * @param metadataCallback
   */
  debug: (message: string, metadataCallback?: LogMessageMetadataCallback) => ILogger;

  /**
   * Log a message with the info level. See Logger.log for more information.
   * @param message
   * @param metadataCallback
   */
  info: (message: string, metadataCallback?: LogMessageMetadataCallback) => ILogger;

  /**
   * Log a message with the warn level. See Logger.log for more information.
   * @param message
   * @param metadataCallback
   */
  warn: (message: string, metadataCallback?: LogMessageMetadataCallback) => ILogger;

  /**
   * Log a message with the error level. See Logger.log for more information.
   * @param message
   * @param metadataCallback
   */
  error: (message: string, metadataCallback?: LogMessageMetadataCallback) => ILogger;

  /**
   * Register metadata on the logger. The metadata is associated with all log messages originating
   * from this logger. It returns the same logger instance on which this method was called in order
   * to support chaining calls (i.e logger.metadata(...).log(...) )
   * @param metadata
   */
  metadata: (metadata: LogMessageMetadata) => ILogger;
  /**
   * Create a new logger instance with an ID generated from the current logger's ID with the provided
   * id appended to it. The new logger also inherits a copy of the current logger's metadata
   * @param id
   */
  extend: (id: string | Array<string>) => ILogger;
}

export interface ILoggerFactory {
  /**
   * Instantiate a new logger with the provided path and metadata. It takes care of configuring
   * the logger's level based on a configuration defined by the implementation.
   * @param path
   * @param metadata
   */
  createLogger: (path: Array<string> | string, metadata?: LogMessageMetadata) => ILogger;
}

export interface LogRouter {
  defaultLevel: LogLevel;
  /**
   * Route the log message to the appropriate transport(s). The logger level
   * @param level The level of the logger from which the message originated, not the message.
   * It may be used by a transport to determine what to do with the message.
   * @param message
   */
  log: (level: LogLevel, message: InternalLogMessage) => void;
}

export interface LogTransport {
  /**
   * Push the log message to a sink using a specific protocol.
   * @param level
   * @param message
   */
  log: (level: LogLevel, message: InternalLogMessage) => void;

  /**
   * Write all pending log messages to the transport
   */
  flush(): Promise<void>;
}
